import React, { useState, Dispatch, SetStateAction } from "react";
import i18n from "../../i18n/config";
import { GetPaymentDisbursementHistoryResponse, PaymentDisbursementHistoryItem } from "../../types/api/user";
import { PaymentDisbursementHistorySearchParams } from "../../types/paymentDisbursementHistory";
import { generateUniqueInteger } from "../../services/formatter";
import InputSkeletonGroup from "../Common/Loaders/InputSkeleton";

const ResultsTable = ({ results, searchParams, setSearchParams, submitting }: {
  results: GetPaymentDisbursementHistoryResponse | null,
  searchParams: PaymentDisbursementHistorySearchParams,
  setSearchParams: Dispatch<SetStateAction<PaymentDisbursementHistorySearchParams>>,
  submitting: boolean
}) => {
  const [active, setActive] = useState({
    column: "DateTime",
    sortOrder: 1
  })

  if (results == null) {
    return null;
  }

  const handleClick = (column: string, sortOrder: number) => {
    setActive({
      column,
      sortOrder
    })

    setSearchParams({
      ...searchParams,
      sortColumn: column,
      sortOrder: sortOrder,
      submitSearch: true
    })
  }

  const isActive = (column: string, sortOrder: number) => {
    return column === active.column && active.sortOrder === sortOrder;
  }

  const skeletonLoader = () => {

    const skeletonRow = () => (
      <tr>
        {
          Array.from({ length: 7 }).map((_) => (
            <td key={generateUniqueInteger()}><InputSkeletonGroup numInputs={1} direction='vertical' hideLabels={true} /></td>
          ))
        }
      </tr>
    )

    return (
      <>
        {Array.from({ length: 10 }).map(() => (
          <React.Fragment key={generateUniqueInteger()}>{skeletonRow()}</React.Fragment>
        ))}
      </>
    )
  }

  const renderSortButtons = (column: string) => {
    return (
      <div className="sort-buttons">
        <button className={`no-styling spacing-right-small ${isActive(column, 0) ? "active" : ""}`} onClick={() => handleClick(column, 0)}>
          ▲
        </button>

        <button className={`no-styling ${isActive(column, 1) ? "active" : ""}`} onClick={() => handleClick(column, 1)}>
          ▼
        </button>
      </div>
    )
  }

  return (
    <div className="overflow-scroll">
      <table className="table old-navigator-style payments">
        <thead>
          <tr>
            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_1")}
                {renderSortButtons("DateTime")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_2")}
                {renderSortButtons("Description")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_3")}
                {renderSortButtons("Type")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_4")}
                {renderSortButtons("PaymentId")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_5")}
                {renderSortButtons("Status")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_6")}
                {renderSortButtons("ServiceFee")}
              </div>
            </th>

            <th>
              <div className="flex-row justify-content-between align-items-center">
                {i18n.t("PAGES.PAYMENTS.TABLE.COL_HEADING_7")}
                {renderSortButtons("Amount")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {submitting &&
            <>
              {skeletonLoader()}
            </>
          }

          {!submitting && results.result.map((item: PaymentDisbursementHistoryItem) => (
            <tr key={generateUniqueInteger()} className={item.type.includes("Payment") ? "highlighted" : ""}>
              <td>{item.dateTimeString}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: item.description }} />
              </td>
              <td>{item.type}</td>
              <td>{item.paymentId}</td>
              <td>{item.status}</td>
              <td>{item.serviceFee}</td>
              <td>{item.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ResultsTable;